// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
import { parseContourInterval } from '../useLayerTypeDetection';
// Monochrome contour rendering: redraw a contour layer in a single color with
// topographic hierarchy — index (every 5th) lines solid + thick, intermediate
// lines dashed + thin — so it stacks legibly over other layers instead of
// competing as an elevation color ramp.
//
// This is a render-time restyle only. It never touches the stored KML; the
// original baked material + width are stashed on each entity so a toggle-off
// restores the exact prior look. The index/intermediate split is re-derived from
// the layer's own interval (NOT the baked line width, which the backend computes
// with an absolute `int(elev) % 5` rule that is wrong for feet and sub-metre
// intervals — see Phase 0 in CONTOUR_MONOCHROME_PLAN.md).
const FT_TO_M = 0.3048;
// Cartographic convention: an index (major) contour every 5th line.
const INDEX_EVERY = 5;
// The weight slider means the same thing in monochrome as in normal vector mode:
// it is the (intermediate) base line width in px. Index lines are drawn a fixed
// ratio thicker so the hierarchy still reads. So weight=1 gives 1px intermediate
// lines that match a 1px coloured layer exactly, with 2.5px index lines.
const INDEX_TO_INTERMEDIATE_RATIO = 2.5;
const DEFAULT_BASE_WIDTH = 1.0;
const DASH_LENGTH = 8.0;
const baseWidthFromWeight = (weight) => weight && weight > 0 ? weight : DEFAULT_BASE_WIDTH;
// Default to the palette's black so the colour picker highlights a swatch.
export const DEFAULT_MONOCHROME_COLOR = '#000000';
// Stash keys for the original baked appearance, so toggle-off is lossless.
const ORIG = '_lsContourMonoOriginal';
// Entities flagged with this own their own appearance temporarily (a contour
// highlight has recoloured or hidden them). The monochrome/opacity restyle
// skips them so it doesn't clobber the highlight.
export const HIGHLIGHTED_CONTOUR_FLAG = '_lsHasHighlight';
const ELEVATION_PROP_NAMES = ['elev', 'height', 'elevation', 'altitude', 'z'];
// elevationMetersFromEntity reads a contour line's elevation in metres. The
// backend writes ELEV/HEIGHT (metres) into KML ExtendedData, which Cesium exposes
// as entity.properties; the placemark name ("Contour 100.0m") is the fallback.
const elevationMetersFromEntity = (entity) => {
    var _a;
    try {
        const props = entity.properties;
        if (props && Array.isArray(props.propertyNames)) {
            for (const propName of props.propertyNames) {
                if (ELEVATION_PROP_NAMES.includes(String(propName).toLowerCase())) {
                    const raw = ((_a = props[propName]) === null || _a === void 0 ? void 0 : _a.getValue) ? props[propName].getValue() : props[propName];
                    const n = parseFloat(String(raw));
                    if (isFinite(n))
                        return n;
                }
            }
        }
    }
    catch (_b) {
        // fall through to name parsing
    }
    const name = entity.name;
    if (typeof name === 'string') {
        const m = name.match(/(-?\d+(?:\.\d+)?)\s*m?\s*$/i);
        if (m) {
            const n = parseFloat(m[1]);
            if (isFinite(n))
                return n;
        }
    }
    return null;
};
// intervalMetersForLayer converts a contour layer's interval to metres, e.g.
// "contours_2ft_display.kml" -> 0.6096, "1m" -> 1. Null if unparseable.
export const intervalMetersForLayer = (layerName) => {
    const parsed = parseContourInterval(layerName);
    if (!parsed)
        return null;
    return parsed.unit === 'ft' ? parsed.value * FT_TO_M : parsed.value;
};
// isIndexLine is true when this elevation falls on every 5th contour line for the
// layer's interval — the true "every Nth line" definition, correct for any unit.
const isIndexLine = (elevationM, intervalM) => {
    const ordinal = Math.round(elevationM / intervalM);
    return ((ordinal % INDEX_EVERY) + INDEX_EVERY) % INDEX_EVERY === 0;
};
const stashOriginal = (entity) => {
    if (entity[ORIG] === undefined) {
        entity[ORIG] = {
            material: entity.polyline.material,
            width: entity.polyline.width
        };
    }
};
// restyleEntity is the single authority for a monochrome contour line's polyline
// appearance: solid+thick for index lines, dashed+thin for intermediate lines,
// with opacity baked into the colour. intervalM === null means the interval could
// not be parsed, so we degrade to a flat recolour (solid, original width) rather
// than guess the hierarchy.
const restyleEntity = (entity, intervalM, color, opacity, baseWidth) => {
    if (!entity.polyline)
        return;
    stashOriginal(entity);
    const c = color.withAlpha(opacity);
    if (intervalM === null) {
        entity.polyline.material = new Cesium.ColorMaterialProperty(c);
        return;
    }
    const elevationM = elevationMetersFromEntity(entity);
    const index = elevationM !== null && isIndexLine(elevationM, intervalM);
    if (index) {
        entity.polyline.material = new Cesium.ColorMaterialProperty(c);
        entity.polyline.width = new Cesium.ConstantProperty(baseWidth * INDEX_TO_INTERMEDIATE_RATIO);
    }
    else {
        entity.polyline.material = new Cesium.PolylineDashMaterialProperty({
            color: c,
            dashLength: DASH_LENGTH
        });
        entity.polyline.width = new Cesium.ConstantProperty(baseWidth);
    }
};
// applyMonochromeContours restyles every polyline entity in a loaded contour
// data source. weight is the base (intermediate) line width in px — same meaning
// as the normal vector weight slider — with index lines INDEX_TO_INTERMEDIATE_RATIO
// thicker. Safe to call repeatedly; originals are only stashed once.
export const applyMonochromeContours = (dataSource, layerName, color, opacity, weight) => {
    var _a;
    const entities = (_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values;
    if (!entities)
        return;
    const cesiumColor = Cesium.Color.fromCssColorString(color) || Cesium.Color.BLACK;
    const intervalM = intervalMetersForLayer(layerName);
    const baseWidth = baseWidthFromWeight(weight);
    for (const entity of entities) {
        if (!entity.polyline)
            continue;
        if (entity[HIGHLIGHTED_CONTOUR_FLAG])
            continue;
        restyleEntity(entity, intervalM, cesiumColor, opacity, baseWidth);
    }
};
// restoreBakedContours reverts a data source to its original baked material +
// width (the elevation ramp) for entities that were previously restyled. Returns
// true if anything was restored, so callers know whether the generic opacity path
// still needs to run afterwards.
export const restoreBakedContours = (dataSource) => {
    var _a;
    const entities = (_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values;
    if (!entities)
        return false;
    let restored = false;
    for (const entity of entities) {
        if (entity[ORIG] !== undefined && entity.polyline) {
            entity.polyline.material = entity[ORIG].material;
            entity.polyline.width = entity[ORIG].width;
            entity[ORIG] = undefined;
            restored = true;
        }
    }
    return restored;
};
